import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { initialPortfolioState } from 'store/initial-states';
import {
  PortfolioResponse,
  PortfolioSettings
} from 'interfaces/project.interface';
import { patchPortfolioProjectFromUpdate } from 'utils/portfolio.util';
import { mergeTopPriorityTasksFromProjects } from 'utils/portfolio-top-priority-task';
import {
  fetchMoreProjects,
  fetchProjects,
  updateProject
} from 'store/reducer/project/extra';
import {
  fetchPortfolio,
  fetchPortfolioForecast,
  fetchPortfolioSettings,
  updatePortfolioSettings
} from './extra';

const portfolio = createSlice({
  name: 'portfolio',
  initialState: initialPortfolioState,
  reducers: {
    updatePortfolio: (
      state,
      { payload }: PayloadAction<PortfolioResponse | null>
    ) => {
      state.portfolio = payload;
    },
    updateSettings: (
      state,
      { payload }: PayloadAction<PortfolioSettings | null>
    ) => {
      state.settings = payload;
    },
    clearTopPriorityTasks: (state) => {
      state.topPriorityTasksByProjectId = {};
    },
    clearTopPriorityTaskForProject: (
      state,
      { payload: projectId }: PayloadAction<string>
    ) => {
      const { [projectId]: _removed, ...remaining } =
        state.topPriorityTasksByProjectId;
      state.topPriorityTasksByProjectId = remaining;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(fetchPortfolio.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPortfolio.fulfilled, (state, { payload }) => {
        state.portfolio = payload;
        state.isLoading = false;
      })
      .addCase(fetchPortfolio.rejected, (state) => {
        state.portfolio = null;
        state.isLoading = false;
      })
      .addCase(fetchPortfolioForecast.pending, (state) => {
        state.isForecastLoading = true;
      })
      .addCase(fetchPortfolioForecast.fulfilled, (state, { payload }) => {
        state.forecast = payload;
        state.isForecastLoading = false;
      })
      .addCase(fetchPortfolioForecast.rejected, (state) => {
        state.forecast = null;
        state.isForecastLoading = false;
      })
      .addCase(fetchPortfolioSettings.pending, (state) => {
        state.isSettingsLoading = true;
      })
      .addCase(fetchPortfolioSettings.fulfilled, (state, { payload }) => {
        state.settings = payload;
        state.isSettingsLoading = false;
      })
      .addCase(fetchPortfolioSettings.rejected, (state) => {
        state.isSettingsLoading = false;
      })
      .addCase(updatePortfolioSettings.pending, (state) => {
        state.isSavingSettings = true;
      })
      .addCase(updatePortfolioSettings.fulfilled, (state, { payload }) => {
        if (payload) {
          state.settings = payload;
        }
        state.isSavingSettings = false;
      })
      .addCase(updatePortfolioSettings.rejected, (state) => {
        state.isSavingSettings = false;
      })
      .addCase(fetchProjects.fulfilled, (state, { payload }) => {
        if (!payload?.data) {
          return;
        }
        state.topPriorityTasksByProjectId = mergeTopPriorityTasksFromProjects(
          state.topPriorityTasksByProjectId,
          payload.data
        );
      })
      .addCase(fetchMoreProjects.fulfilled, (state, { payload }) => {
        if (!payload?.data) {
          return;
        }
        state.topPriorityTasksByProjectId = mergeTopPriorityTasksFromProjects(
          state.topPriorityTasksByProjectId,
          payload.data
        );
      })
      .addCase(updateProject.fulfilled, (state, { payload }) => {
        if (!payload || !state.portfolio) {
          return;
        }
        state.portfolio = patchPortfolioProjectFromUpdate(
          state.portfolio,
          payload
        );
      });
  }
});

export const {
  updatePortfolio,
  updateSettings,
  clearTopPriorityTasks,
  clearTopPriorityTaskForProject
} = portfolio.actions;

export default portfolio.reducer;
